<template>
  <div class="dashboard-container animated fadeIn">
    <!-- Stats Grid -->
    <div class="stats-grid mb-4">
      <div class="stat-card">
        <div class="stat-icon">
          <b-icon icon="puzzle"></b-icon>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('components') }}</div>
          <div class="stat-value">{{ stats.components }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <b-icon icon="people"></b-icon>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('users') }}</div>
          <div class="stat-value">{{ stats.users }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <b-icon icon="shield-lock"></b-icon>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('roles') }}</div>
          <div class="stat-value">{{ stats.roles }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <i class="icon-energy"></i>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('thunder') }}</div>
          <div class="stat-value">{{ stats.thunder }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <img src="/img/developer/forge.png" width="24" />
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('forge') }}</div>
          <div class="stat-value">{{ stats.forge }}</div>
        </div>
      </div>

      <div class="stat-card" v-if="isSparkActive">
        <div class="stat-icon">
          <i class="icon-fire"></i>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('spark') }}</div>
          <div class="stat-value">{{ stats.spark }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <img src="/img/developer/ratchet.svg" width="24" />
        </div>
        <div class="stat-content">
          <div class="stat-label">Ratchet</div>
          <div class="stat-value">{{ stats.ratchet }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <b-icon icon="puzzle"></b-icon>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('integrationslabel') }}</div>
          <div class="stat-value">{{ stats.integrations }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <i class="icon-share"></i>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('workflow.title') }}</div>
          <div class="stat-value">{{ stats.workflows }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <b-icon icon="code"></b-icon>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('functions.title') }}</div>
          <div class="stat-value">{{ stats.functions }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon">
          <b-icon icon="folder"></b-icon>
        </div>
        <div class="stat-content">
          <div class="stat-label">{{ $t('workspaces') }}</div>
          <div class="stat-value">{{ stats.workspaces }}</div>
        </div>
      </div>
    </div>

    <!-- Running Workflows Stats -->
    <b-card class="mb-4" v-if="$can('manage base_workflows')">
      <RunningWorkflowStats
        title="Running Workflows"
        :autoRefresh="true"
        :refreshInterval="10000"
      />
    </b-card>

    <!-- Workflow Statistics Chart -->
    <b-card class="workflow-chart-card" v-if="$can('manage base_workflows')">
      <WorkflowStatistics
        :selectedPeriod="selectedDays"
        :periods="[7, 14, 30, 90]"
        :chartTitle="$t('executionTrends')"
        @change-period="selectedDays = $event"
      />
    </b-card>

    <!-- Rate Limit & Throttle Monitoring -->
    <b-row class="mt-4">
      <b-col lg="6" class="mb-4">
        <RateLimitChart
          :rateLimitData="rateLimitData"
          :loading="loadingLimits"
        />
      </b-col>
      <b-col lg="6" class="mb-4">
        <ThrottleMonitor
          :throttleData="throttleData"
          :loading="loadingLimits"
        />
      </b-col>
    </b-row>

    <!-- Original Widgets -->
    <b-row v-if="widgets.length > 0" class="mt-4">
      <video-tutorial v-if="widgets.length < 1"/>
      <Base v-for="widget in widgets" :widget="widget" v-bind:key="widget.uuid"/>
    </b-row>
  </div>
</template>

<script>
import Base from '../components/widgets/Base'
import VideoTutorial from '../components/widgets/VideoTutorial'
const WorkflowStatistics = () => import(/* webpackChunkName: "workflow-stats" */ '@/components/workflow/WorkflowStatistics')
const RunningWorkflowStats = () => import(/* webpackChunkName: "workflow-stats" */ '@/components/workflow/RunningWorkflowStats')
const RateLimitChart = () => import(/* webpackChunkName: "charts" */ '@/components/dashboard/RateLimitChart')
const ThrottleMonitor = () => import(/* webpackChunkName: "charts" */ '@/components/dashboard/ThrottleMonitor')

export default {
  name: 'dashboard',
  components: {
    Base,
    VideoTutorial,
    WorkflowStatistics,
    RunningWorkflowStats,
    RateLimitChart,
    ThrottleMonitor
  },
  data: function () {
    return {
      stats: {
        components: 0,
        users: 0,
        roles: 0,
        thunder: 0,
        forge: 0,
        spark: 0,
        ratchet: 0,
        integrations: 0,
        workflows: 0,
        functions: 0,
        workspaces: 0
      },
      selectedDays: 7,
      rateLimitData: {
        requests_per_minute: 100,
        window_seconds: 60,
        current_requests: 0,
        history: []
      },
      throttleData: {
        max_concurrent: 50,
        current_concurrent: 0
      },
      loadingLimits: false,
      refreshInterval: null
    }
  },
  computed: {
    widgets() {
      let w = this.$store.getters.widgets

      return w.filter(i => {
        return i.is_active
      })
    },
    isSparkActive() {
      return this.$store.getters.isIntegrationActive('protokol-spark')
    }
  },
  methods: {
    async loadStats() {
      try {
        // Load components count from store
        await this.$store.dispatch('fetchComponents')
        const components = this.$store.state.components.components || []
        this.stats.components = components.length

        // Load users count
        const usersResp = await this.$project.users()
        this.stats.users = usersResp.data ? usersResp.data.length : 0

        // Load roles count from store
        await this.$store.dispatch('fetchRoles')
        const roles = this.$store.getters.getRoles || []
        this.stats.roles = roles.length

        // Load integrations count from store
        await this.$store.dispatch('fetchIntegrations')
        const integrations = this.$store.getters.getIntegrations || []
        this.stats.integrations = integrations.length

        // Load thunder count
        try {
          const thunderResp = await this.$http.get('/v3/system/thunder')
          this.stats.thunder = thunderResp.data ? thunderResp.data.length : 0
        } catch (err) {
          this.stats.thunder = 0
        }

        // Load forge count
        try {
          const forgeResp = await this.$http.get('/luma/appservice/v1/forge')
          this.stats.forge = forgeResp.data ? forgeResp.data.length : 0
        } catch (err) {
          this.stats.forge = 0
        }

        // Load spark count
        if (this.isSparkActive) {
          try {
            const sparkResp = await this.$sandbox.spark().all()
            this.stats.spark = sparkResp.data ? sparkResp.data.length : 0
          } catch (err) {
            this.stats.spark = 0
          }
        }

        // Load ratchet count
        try {
          const ratchetResp = await this.$ratchetApi.get('/connection')
          this.stats.ratchet = ratchetResp.data ? ratchetResp.data.length : 0
        } catch (err) {
          this.stats.ratchet = 0
        }

        // Load workflows count
        try {
          const workflowsResp = await this.$workflowApi.get('/workflows')
          this.stats.workflows = workflowsResp.data ? workflowsResp.data.length : 0
        } catch (err) {
          this.stats.workflows = 0
        }

        // Load functions count
        try {
          const functionsResp = await this.$functions.all()
          this.stats.functions = functionsResp.data ? functionsResp.data.length : 0
        } catch (err) {
          this.stats.functions = 0
        }

        // Load workspaces count from current project
        const currentProject = this.$store.getters.getCurrentProject
        if (currentProject && currentProject.workspaces) {
          this.stats.workspaces = currentProject.workspaces.length
        } else {
          this.stats.workspaces = 0
        }
      } catch (err) {
        this.$debugError(err)
      }
    },

    async loadLimits() {
      this.loadingLimits = true
      try {
        const response = await this.$http.get('/v1/project/limits')
        if (response.data) {
          if (response.data.rate_limit) {
            this.rateLimitData = response.data.rate_limit
          }
          if (response.data.throttle) {
            this.throttleData = response.data.throttle
          }
        }
      } catch (err) {
        console.error('Error loading limits:', err)
      } finally {
        this.loadingLimits = false
      }
    },

    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.loadLimits()
      }, 10000)
    },

    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    }
  },

  async mounted() {
    await this.loadStats()
    await this.loadLimits()
    this.startAutoRefresh()
  },

  beforeDestroy() {
    this.stopAutoRefresh()
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.dashboard-container {
  width: 100%;
  height: 100%;
  padding: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: $dark-background;
  border: 1px solid $dark-border2;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
    border-color: $protokol-royal-active;
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    background: rgba(102, 126, 234, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    color: $protokol-royal-active;
    flex-shrink: 0;

    img {
      width: 24px;
      height: 24px;
    }

    .icon-energy {
      color: yellow;
    }

    .icon-fire {
      color: orange;
    }

    .icon-share {
      color: $protokol-royal-active;
    }
  }

  .stat-content {
    flex: 1;
  }

  .stat-label {
    font-size: 12px;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .stat-value {
    font-size: 28px;
    font-weight: 700;
    color: var(--text-primary);
  }
}

.workflow-chart-card {
  background: $dark-background;
  border: 1px solid $dark-border2;

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;

    h4 {
      margin: 0;
      color: var(--text-primary);
    }

    .period-selector {
      display: flex;
      gap: 8px;
    }
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }

  .stat-card {
    padding: 16px;

    .stat-icon {
      width: 40px;
      height: 40px;
      font-size: 20px;
    }

    .stat-value {
      font-size: 24px;
    }
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start !important;
  }
}
</style>
